import { Fragment, useMemo } from "react";
import { useBanner, useBannerButtonCallbacks, BannerButton } from "../../..";
import { CONTEXT_LATEST_DESIGN_VERSION } from "../../../types";

const BannerButtonList = () => {
  const banner = useBanner();
  const {
    isConsentRecord,
    activeAction,
    bodyDesign: {
      acceptEssentialsUseAcceptAll,
      acceptAllOneRowLayout
    },
    decision: {
      showGroups,
      groupsFirstView,
      saveButton,
      acceptAll,
      acceptEssentials,
      acceptIndividual,
      buttonOrder: buttonOrderString
    },
    texts: {
      acceptAll: acceptAllText,
      acceptEssentials: acceptEssentialsText,
      acceptIndividual: acceptIndividualText
    },
    saveButton: {
      type: saveButtonType,
      useAcceptAll: saveButtonUseAcceptAll
    },
    individualTexts: {
      save
    },
    individualPrivacyOpen,
    didGroupFirstChange,
    productionNotice,
    buttonClicked = "",
    designVersion = CONTEXT_LATEST_DESIGN_VERSION
  } = banner;
  const useAcceptEssentialsUseAcceptAll = acceptEssentialsUseAcceptAll && acceptAll === acceptEssentials;
  const useSaveButtonUseAcceptAll = saveButtonUseAcceptAll && acceptAll === saveButtonType; // Calculate order of the buttons depending on a string like `all,essential,save,individual`

  const {
    all: buttonOrderAll,
    essential: buttonOrderEssential,
    individual: buttonOrderIndividual,
    save: buttonOrderSave
  } = useMemo(() => {
    const buttonOrder = buttonOrderString.split(",");
    const buttonToOrder = buttonOrder.reduce((previous, curr) => {
      previous[curr] = buttonOrder.indexOf(curr);
      return previous;
    }, {});
    const orderToButton = buttonOrder.reduce((previous, curr) => {
      previous[buttonOrder.indexOf(curr)] = curr;
      return previous;
    }, {}); // If one-row layout is enabled, reverse the first two orders so the first button gets moved to the right

    if (acceptAllOneRowLayout) {
      buttonToOrder[orderToButton[0]] = 1;
      buttonToOrder[orderToButton[1]] = 0;
    }

    return buttonToOrder;
  }, [buttonOrderString, acceptAllOneRowLayout]);
  const isPro = process.env.PLUGIN_CTX === "pro";
  const {
    buttonClickedAll,
    buttonClickedEssentials,
    buttonClickedCustom,
    acceptAll: handleAll,
    acceptEssentials: handleEssentials,
    acceptIndividual: handleIndividual,
    openIndividualPrivacy
  } = useBannerButtonCallbacks(); // We expect a reload of the page for the "change" action (keeps also dialog open)

  const busyOnClick = activeAction === "change" && !isConsentRecord;
  const groupsFirstViewEnabled = showGroups && groupsFirstView;
  return h(Fragment, null, !individualPrivacyOpen && isPro && groupsFirstViewEnabled && saveButton === "afterChangeAll" && didGroupFirstChange ? h(BannerButton, {
    onClick: handleIndividual,
    type: acceptAll,
    inlineStyle: "acceptAll",
    framed: buttonClicked === buttonClickedCustom,
    busyOnClick: busyOnClick,
    order: buttonOrderAll
  }, save) : h(BannerButton, {
    onClick: handleAll,
    type: acceptAll,
    inlineStyle: "acceptAll",
    framed: buttonClicked === buttonClickedAll,
    busyOnClick: busyOnClick,
    order: buttonOrderAll
  }, acceptAllText), (activeAction !== "change" || designVersion > 2) && h(BannerButton, {
    onClick: handleEssentials,
    type: useAcceptEssentialsUseAcceptAll ? acceptAll : acceptEssentials,
    inlineStyle: useAcceptEssentialsUseAcceptAll ? "acceptAll" : "acceptEssentials",
    framed: buttonClicked === buttonClickedEssentials,
    busyOnClick: busyOnClick,
    order: buttonOrderEssential
  }, acceptEssentialsText), (individualPrivacyOpen || isPro && groupsFirstViewEnabled && (saveButton === "always" || saveButton === "afterChange" && didGroupFirstChange)) && h(BannerButton, {
    onClick: handleIndividual,
    type: useSaveButtonUseAcceptAll ? acceptAll : saveButtonType,
    inlineStyle: useSaveButtonUseAcceptAll ? "acceptAll" : "save",
    framed: buttonClicked === buttonClickedCustom,
    busyOnClick: busyOnClick,
    order: buttonOrderSave
  }, save), !individualPrivacyOpen && h(BannerButton, {
    type: acceptIndividual,
    onClick: openIndividualPrivacy,
    inlineStyle: "acceptIndividual",
    framed: buttonClicked.startsWith("ind_"),
    busyOnClick: busyOnClick,
    order: buttonOrderIndividual
  }, acceptIndividualText), productionNotice);
};

export { BannerButtonList };